import { useMemo, type ReactNode } from 'react';
import { CitationLink } from './CitationLink';

type Props = {
  text: string;
  invalidCitations: string[];
  isStreaming: boolean;
};

/**
 * Renders the streamed answer with inline `(S:V)` and `(S:V-V)` citations
 * parsed into <CitationLink>s. Pure-React parser; no innerHTML.
 *
 * Re-parses on every keystroke of streaming — fine for response sizes
 * we expect (3–6 sentences). If we ever need to optimize, memoize per
 * paragraph.
 */
const CITATION_PATTERN = /\((\d{1,3}):(\d{1,3})(?:-(\d{1,3}))?\)/g;

export function StreamingAnswer({
  text,
  invalidCitations,
  isStreaming,
}: Props) {
  const invalidSet = useMemo(
    () => new Set(invalidCitations),
    [invalidCitations],
  );

  const segments = useMemo(() => renderWithCitations(text, invalidSet), [
    text,
    invalidSet,
  ]);

  if (!text) return null;

  return (
    <div
      aria-live="polite"
      aria-busy={isStreaming}
      className="font-serif text-lg leading-relaxed text-ink-800 dark:text-ink-100 whitespace-pre-wrap"
    >
      {segments}
      {isStreaming && <Cursor />}
    </div>
  );
}

function renderWithCitations(
  text: string,
  invalidSet: Set<string>,
): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(CITATION_PATTERN)) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      out.push(text.slice(lastIndex, matchIndex));
    }
    const surah = Number(match[1]);
    const startAyah = Number(match[2]);
    const endAyah = match[3] ? Number(match[3]) : undefined;
    const raw = match[0];
    out.push(
      <CitationLink
        key={`c-${key++}`}
        surah={surah}
        startAyah={startAyah}
        endAyah={endAyah}
        invalid={invalidSet.has(raw)}
      />,
    );
    lastIndex = matchIndex + raw.length;
  }
  if (lastIndex < text.length) out.push(text.slice(lastIndex));
  return out;
}

function Cursor() {
  return (
    <span
      aria-hidden="true"
      className="inline-block ml-0.5 w-[2px] h-[1.1em] align-text-bottom bg-accent animate-pulse"
    />
  );
}
